import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { Stock } from "../model/stock";
import { Observable } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { UserService } from "./user.service";
import { EventData } from "./event-data-transfer.service";
import { EncryptedTextDto } from "../model/encryptedTextDto";
import { ProjectDuplicate } from "../model/project-duplicate";

@Injectable({
  providedIn: "root",
})
export class DatasService {
  private _styles: any;
  private _models: any;
  private _layouts: any;
  private _ovenConfigurations: any;
  private _furnitures: any;
  private _isInit: boolean = false;

  constructor(
    public translate: TranslateService,
    private http: HttpClient,
    private userService: UserService
  ) {}

  public init = (): Promise<void> => {
    if (!this.userService.country)
      return Promise.reject(new Error("No country defined"));
    if (this._isInit) return Promise.resolve();

    return new Promise((resolve: any, reject: any) => {
      this.http
        .get(
          `datas/datas_${this.userService.country.toUpperCase()}.json?r=` +
            Math.random()
        )
        .subscribe({
          next: (responseDatas: any) => {
            this._styles = responseDatas.styles;
            this._models = responseDatas.models;
            this._layouts = responseDatas.layouts;
            this._ovenConfigurations = responseDatas.ovenConfigurations;
            this._furnitures = responseDatas.furnitures;
            this._isInit = true;
            resolve();
          },
          error: (error: any) => {
            this._displayError();
            reject(error);
          },
        });
    });
  };

  async getStyles(): Promise<any> {
    if (!this._styles) {
      await this.init();
    }

    return this._styles;
  }

  async getModels(): Promise<any> {
    if (!this._models) {
      await this.init();
    }

    return this._models;
  }

  async getLayouts(): Promise<any> {
    if (!this._layouts) {
      await this.init();
    }

    return this._layouts;
  }

  async getOvenConfigurations(): Promise<any> {
    if (!this._ovenConfigurations) {
      await this.init();
    }

    return this._ovenConfigurations;
  }

  async getFurnitures(): Promise<any> {
    if (!this._furnitures) {
      await this.init();
    }

    return this._furnitures;
  }

  private _displayError(errorMsg: string = null) {
    let _m = errorMsg || this.translate.instant("generic.maintenance");
    document.getElementById("launch-msg").innerHTML = _m;
  }

  getProjectDuplicate(projectId: number): Promise<ProjectDuplicate> {
    return new Promise((resolve, reject) => {
      let headers = new HttpHeaders().set("Content-Type", "application/json");

      let params: any = {
        method: "GET",
        url: environment.backEnd.baseApi + "ProjectDuplicate/" + projectId,
        headers: headers
      }

      this.generateBackendApiRequest(params).subscribe({
        next: (projectDuplicate: ProjectDuplicate) => resolve(projectDuplicate),
        error: (errorResponse: any) => {
          console.log("getProjectDuplicate.errorResponse", errorResponse);
          reject(errorResponse);
        },
      })
    })
  }

  insertProjectDuplicate(originalProjectId: number, duplicateProjectId: number): Promise<ProjectDuplicate> {
    return new Promise((resolve, reject) => {
      let headers = new HttpHeaders().set("Content-Type", "application/json");

      let params: any = {
        method: "POST",
        url: environment.backEnd.baseApi + "ProjectDuplicate",
        headers: headers,
        body: {
          originalProjectId: originalProjectId,
          duplicateProjectId: duplicateProjectId
        }
      }

      this.generateBackendApiRequest(params).subscribe({
        next: (projectDuplicate: ProjectDuplicate) => resolve(projectDuplicate),
        error: (errorResponse: any) => {
          console.log("insertProjectDuplicate.errorResponse", errorResponse);
          reject(errorResponse);
        },
      })
    })
  }


  getStockAvailability(eans: string[]): Promise<Stock[]> {
    return new Promise((resolve, reject) => {
      let headers = new HttpHeaders().set("Content-Type", "application/json");

      let params: any = {
        method: "POST",
        url: environment.backEnd.baseApi + "Stocks/Availability",
        headers: headers,
        body: {
          eans: eans,
          country: this.userService.country,
        },
      };

      this.generateBackendApiRequest(params).subscribe({
        next: (availability: any) => resolve(availability),
        error: (errorResponse: any) => {
          console.log("getStockAvailability.errorResponse", errorResponse);
          reject(errorResponse);
        },
      });
    });
  }

  insertEventData(data: EventData): Promise<string> {
    return new Promise((resolve, reject) => {
      let headers = new HttpHeaders().set("Content-Type", "application/json");

      let params: any = {
        method: "POST",
        url:
          environment.backEnd.baseApi +
          environment.backEnd.eventData.apiName +
          "Insert",
        headers: headers,
        body: data,
      };

      this.generateBackendApiRequest(params).subscribe({
        next: (parameters) => resolve(parameters),
        error: (e) => {
          console.log("insertEventData.errorResponse", e);
          reject(e);
        },
      });
    });
  }

  encryptText(originText: string): Promise<string> {
    return new Promise((resolve, reject) => {
      let headers = new HttpHeaders().set("Content-Type", "application/json");

      let params: any = {
        method: "POST",
        url:
          environment.backEnd.baseApi +
          environment.backEnd.eventData.apiName +
          "Encrypt",
        headers: headers,
        body: {
          originText: originText,
        },
      };

      this.generateBackendApiRequest(params).subscribe({
        next: (textEncrypt: EncryptedTextDto) => resolve(textEncrypt.encryptedText),
        error: (errorResponse: any) => {
          console.log("encryptText.errorResponse", errorResponse);
          reject(errorResponse);
        },
      });
    });
  }

  getIFrameParameters(): Promise<string> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders()
        .set("Content-Type", "application/json")
        .set("context", this.userService.context)
        .set("country", this.userService.country);

      let params: any = {
        method: "GET",
        url:
          environment.backEnd.baseApi +
          environment.backEnd.byMe.apiName +
          "IFrameParameters",
        headers: headers,
      };

      this.generateBackendApiRequest(params).subscribe({
        next: (parameters) => resolve(parameters),
        error: (e) => {
          console.log("getIFrameUrl.errorResponse", e);
          reject(e);
        },
      });
    });
  }

  generateBackendApiRequest(params: any): Observable<any> {
    let _options: any = {
      headers: params.headers,
    };

    if (params.responseType) {
      _options.responseType = params.responseType;
    }

    switch (params.method) {
      case "GET":
        return this.http.get(params.url, _options);
      case "POST":
        return this.http.post(params.url, params.body, _options);
      case "PUT":
        return this.http.put(params.url, params.body, _options);
      case "DELETE":
        return this.http.delete(params.url, _options);
      default:
        break;
    }
  }

  public getKitchenGtmData(kitchen: any): any {
    let _kitchen: any = { kitchen_ambiance: "All" };

    if (kitchen.styleId > 0) {
      let _kStyle = this._styles.find((s) => s.id === kitchen.styleId);
      _kitchen.kitchen_ambiance = this.translate.instant(_kStyle.title);
    }

    if (kitchen.modelId === 0) {
      _kitchen.kitchen_model = "All";
    } else if (kitchen.modelId > 0) {
      let _kModel = this._models.find((m) => m.id === kitchen.modelId);
      _kitchen.kitchen_model = _kModel.title;
    }

    if (kitchen.layoutRef) {
      _kitchen.kitchen_layout = kitchen.layoutRef;
      if (_kitchen.kitchen_layout === "") _kitchen.kitchen_layout = "All";
    }

    if (kitchen.ovenConfigurationRef === "") {
      _kitchen.kitchen_oven = "All";
    } else if (kitchen.ovenConfigurationRef) {
      let _kOvenConf = this._ovenConfigurations.find(
        (oc) => oc.id === kitchen.ovenConfigurationRef
      );
      _kitchen.kitchen_oven = this.translate.instant(_kOvenConf.titleRef);
    }

    if (kitchen.furnitureConfiguration === "") {
      _kitchen.kitchen_furniture = "All";
    } else if (kitchen.furnitureConfiguration) {
      let _kFurniture = this._furnitures.find(
        (fc) => fc.id === kitchen.furnitureConfiguration
      );
      _kitchen.kitchen_furniture = this.translate.instant(_kFurniture.titleRef);
    }

    return _kitchen;
  }
}
